// Browsers won't reliably show a video's first frame on their own — iOS WebKit (Safari
// and, since it's forced onto the same engine, Firefox too) shows a plain black box until
// playback starts. Every real video player ships a static poster instead of relying on
// browser frame-extraction, so we generate one ourselves at upload time, client-side.

import { api } from "./api";

/** Grabs a frame from a local video file as a JPEG blob, for a poster image. */
export function captureVideoThumbnail(file: File): Promise<Blob | null> {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";
    // WebKit (Safari/iOS) refuses to decode a frame for a merely-seeked, never-played video —
    // drawImage silently produces solid black. It must actually render at least one frame
    // first, so the element has to be attached to the document (a detached <video> never
    // renders), which in turn means keeping it out of sight without display:none/visibility:
    // hidden — either of those also skips rendering, right back to a black canvas.
    Object.assign(video.style, {
      position: "fixed",
      top: "0",
      left: "0",
      width: "2px",
      height: "2px",
      opacity: "0.01",
      pointerEvents: "none",
    });
    document.body.appendChild(video);
    const objectUrl = URL.createObjectURL(file);

    let settled = false;
    const timeout = setTimeout(() => finish(null), 8000);
    function finish(blob: Blob | null) {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      URL.revokeObjectURL(objectUrl);
      video.remove();
      resolve(blob);
    }

    function capture() {
      const canvas = document.createElement("canvas");
      const maxWidth = 640;
      const scale = video.videoWidth > maxWidth ? maxWidth / video.videoWidth : 1;
      canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
      canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
      const ctx = canvas.getContext("2d");
      if (!ctx) return finish(null);
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => finish(blob), "image/jpeg", 0.82);
    }

    video.addEventListener("loadedmetadata", () => {
      video.play().then(() => {
        // Let a frame or two actually render, then pause and seek a hair past frame 0
        // (some clips fade in from black on the very first frame) before capturing.
        setTimeout(() => {
          video.pause();
          const duration = Number.isFinite(video.duration) ? video.duration : 1;
          video.addEventListener("seeked", capture, { once: true });
          video.currentTime = Math.min(0.3, duration / 2);
        }, 120);
      }, () => finish(null));
    });
    video.addEventListener("error", () => finish(null));
    video.src = objectUrl;
  });
}

/** Captures + uploads a poster for a local video file. Never throws — a missing poster
 *  is a lesser evil than failing the whole video upload over it. */
export async function uploadVideoThumbnail(file: File): Promise<string | null> {
  try {
    const blob = await captureVideoThumbnail(file);
    if (!blob) return null;
    const { url } = await api.uploadImage(new File([blob], "thumb.jpg", { type: "image/jpeg" }), () => {});
    return url;
  } catch {
    return null;
  }
}
